import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useContent, useEntry } from "@/lib/queries";
import { answeredCount, totalPrompts, stateLabel } from "@/lib/me";
import { Btn, Card, Overline } from "@/components/kit";
import { Check } from "lucide-react";

export default function MonthOverview() {
  const { id } = useParams();
  const mid = parseInt(id, 10);
  const navigate = useNavigate();
  const { data: content } = useContent();
  const { data: entry } = useEntry(mid);
  if (!content || !entry) return <div className="text-inkfaint text-sm animate-pulse">Loading…</div>;

  const state = content.states.find((s) => s.id === mid);
  if (!state) return <p className="text-inkfaint">State not found.</p>;
  const answers = entry.answers || {};
  const answered = answeredCount(answers, state);
  const total = totalPrompts(state);
  const completed = entry.completed;

  const ctaLabel = completed ? "Revisit this state" : answered > 0 ? `Continue · ${answered}/${total}` : "Begin journaling";

  return (
    <div className="max-w-2xl mx-auto em-fade-up pb-24" data-testid="month-overview">
      <div className="flex items-center justify-between">
        <Overline>{state.id === 0 ? `${state.me} · Baseline` : `${state.me} · Month ${state.id} of 11`}</Overline>
        {completed && (
          <span className="inline-flex items-center gap-1 text-emsuccess text-xs" data-testid="complete-badge">
            <Check className="w-4 h-4" /> Complete
          </span>
        )}
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-3">{state.title}</h1>

      <div className="mt-6 space-y-4">
        {state.intro.map((p, i) => (
          <p key={i} className="text-inksoft leading-relaxed">{p}</p>
        ))}
      </div>

      <Card className="mt-8">
        <Overline>This month's enquiry</Overline>
        <p className="text-ink font-serif text-xl leading-snug mt-2">{state.enquiry}</p>
      </Card>

      <h2 className="text-inkfaint text-sm uppercase tracking-widest mt-10 mb-4">What you'll move through</h2>
      <div className="space-y-2">
        {state.sections.map((s) => (
          <div key={s.id} className="flex items-center justify-between bg-panel border border-line rounded-xl px-4 py-3">
            <span className="text-ink">{s.title}</span>
            <span className="text-faint text-sm">{s.prompts.length} {s.prompts.length === 1 ? "prompt" : "prompts"}</span>
          </div>
        ))}
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-surface/90 backdrop-blur border-t border-line md:pl-0">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-4 flex justify-end">
          <Btn onClick={() => navigate(`/journal/${mid}`)} data-testid="begin-journaling-btn">{ctaLabel}</Btn>
        </div>
      </div>
    </div>
  );
}
